import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  activeOps,
  deserializeEditGraph,
  reduceEditGraph,
  resolveEdit,
  scoreEdit,
  serializeEditGraph,
  validateEditGraphAgainstPack,
  type EditCommand,
  type EditGraph,
  type EditOp,
  type ScoreBreakdown,
} from "@desk/core";
import {
  createGraphForPack,
  resolvePackIdFromQuery,
  type PackId,
  PACK_REGISTRY,
  SHIFT_PACK_ORDER,
} from "../../content/registry.js";
import type { StoreItemId, UnlockId, EquippedResultCardTheme } from "../../game/types.js";
import { resolveResultCardTheme } from "../../game/store-catalog.js";
import { shouldShowReconstructionLabel } from "../../game/disclosure.js";
import { HybridVideoRendererAdapter } from "../../rendering/hybridvideo/HybridVideoRendererAdapter.js";
import { isPreviewCancelledError } from "../lib/preview-cancelled.js";
import { buildEditedTimeline } from "../lib/timeline.js";

const DEFAULT_SEED = 42;

export interface DeskSessionOptions {
  /** Controlled pack; when omitted, reads `?scenario=` / defaults to Vet Day. */
  packId?: PackId;
  seed?: number;
  /** Serialized EditGraph to hydrate; invalid → fresh graph. */
  graphJson?: string | null;
  onGraphChange?: (graphJson: string) => void;
  unlocks?: readonly UnlockId[];
  purchases?: readonly StoreItemId[];
  equippedTheme?: EquippedResultCardTheme;
}

function graphFromJson(packId: PackId, seed: number, graphJson?: string | null): EditGraph {
  const entry = PACK_REGISTRY[packId];
  if (graphJson) {
    try {
      const g = deserializeEditGraph(graphJson);
      validateEditGraphAgainstPack(g, entry.pack);
      return g;
    } catch {
      /* fall through */
    }
  }
  return createGraphForPack(packId, seed);
}

export function useDeskSession(options: DeskSessionOptions = {}) {
  const initialPackId =
    options.packId ??
    (typeof window !== "undefined"
      ? resolvePackIdFromQuery(window.location.search)
      : SHIFT_PACK_ORDER[0]!);
  const seed = options.seed ?? DEFAULT_SEED;

  const [packId, setPackId] = useState<PackId>(initialPackId);
  const entry = PACK_REGISTRY[packId];
  const pack = entry.pack;
  const [graph, setGraph] = useState<EditGraph>(() =>
    graphFromJson(initialPackId, seed, options.graphJson),
  );
  const graphRef = useRef(graph);
  graphRef.current = graph;
  const [tick, setTick] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef(new HybridVideoRendererAdapter());
  const onGraphChangeRef = useRef(options.onGraphChange);
  onGraphChangeRef.current = options.onGraphChange;

  const unlocks = options.unlocks ?? ["cut", "frame", "text", "sound", "cast", "captionDeep", "soundDeep"];
  const purchases = options.purchases ?? [];
  const equippedTheme = options.equippedTheme;

  // MF1: canonical exports (ArtifactPreview/Publish/Compare/Portfolio) are now
  // self-contained — HybridVideoRendererAdapter.exportReel builds its own
  // offscreen video per request, so no live editor adapter is donated here.

  // Sync controlled pack / graph from game state (story transitions).
  useEffect(() => {
    if (options.packId && options.packId !== packId) {
      setPlaying(false);
      setReady(false);
      setExportNote(null);
      setError(null);
      setTick(0);
      adapterRef.current.disposePreview();
      setPackId(options.packId);
      const next = graphFromJson(options.packId, options.seed ?? DEFAULT_SEED, options.graphJson);
      graphRef.current = next;
      setGraph(next);
    }
  }, [options.packId, options.seed, options.graphJson, packId]);

  const resolved = useMemo(() => resolveEdit(graph, pack), [graph, pack]);
  const timeline = useMemo(() => buildEditedTimeline(pack, resolved), [pack, resolved]);
  const score: ScoreBreakdown = useMemo(
    () => scoreEdit(pack, graph, resolved),
    [pack, graph, resolved],
  );
  const showReconstructionLabel = useMemo(
    () => shouldShowReconstructionLabel(pack, resolved),
    [pack, resolved],
  );

  // Narrow testSpeed / perf mirror for Playwright — absent in normal sessions.
    useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const testSpeed =
      params.get("testSpeed") === "1" ||
      params.get("testspeed") === "1" ||
      document.documentElement.dataset.testSpeed === "1";
    const perf = params.get("perf") === "1";
    if (!testSpeed && !perf) return;
    const json = serializeEditGraph(graph);
    document.documentElement.dataset.editGraphJson = json;
    window.__DESK_EDITOR_GRAPH_JSON__ = json;
    return () => {
      delete document.documentElement.dataset.editGraphJson;
      delete window.__DESK_EDITOR_GRAPH_JSON__;
    };
  }, [graph]);

  const emitGraph = useCallback((next: EditGraph) => {
    onGraphChangeRef.current?.(serializeEditGraph(next));
  }, []);

  const applyCommand = useCallback(
    (command: EditCommand) => {
      // Compute reducer result outside setState so the updater stays pure and
      // parent emit/setError never run during React render (StrictMode-safe).
      try {
        const next = reduceEditGraph(graphRef.current, command);
        validateEditGraphAgainstPack(next, pack);
        graphRef.current = next;
        setGraph(next);
        setError(null);
        emitGraph(next);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [pack, emitGraph],
  );

  const applyOp = useCallback(
    (op: EditOp) => applyCommand({ type: "apply", op }),
    [applyCommand],
  );

  const loadPack = useCallback((nextId: PackId) => {
    setPlaying(false);
    setReady(false);
    setExportNote(null);
    setError(null);
    setTick(0);
    adapterRef.current.disposePreview();
    setPackId(nextId);
    const g = createGraphForPack(nextId, seed);
    graphRef.current = g;
    setGraph(g);
    emitGraph(g);
  }, [seed, emitGraph]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let cancelled = false;
    const adapter = adapterRef.current;
    adapter.setTickListener((t) => setTick(t));
    void (async () => {
      try {
        adapter.disposePreview();
        // disposePreview bumps generation; restore listener after cancel barrier.
        adapter.setTickListener((t) => setTick(t));
        validateEditGraphAgainstPack(graph, pack);
        const r = resolveEdit(graph, pack);
        const session = await adapter.createPreview(pack, graph, r, mount);
        if (cancelled) {
          // Effect teardown owns cancellation; do not tear down a newer active preview.
          return;
        }
        setReady(true);
        setError(null);
        setTick(session.currentTick);
      } catch (err) {
        if (cancelled || isPreviewCancelledError(err)) {
          // Stale/cancelled completion must not tear down a newer preview.
          return;
        }
        // Active (non-cancelled) error: clean up this generation's published preview.
        adapter.disposePreview();
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
      adapter.setTickListener(null);
      adapter.disposePreview();
      setReady(false);
    };
    // Recreate preview when pack changes; graph updates go through updatePreviewGraph.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional pack-only remount
  }, [pack, packId]);

  useEffect(() => {
    if (!ready) return;
    try {
      validateEditGraphAgainstPack(graph, pack);
      adapterRef.current.updatePreviewGraph(graph, resolved);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [graph, pack, resolved, ready]);

  const seek = useCallback(async (nextTick: number) => {
    const session = adapterRef.current.getPreviewSession();
    if (!session) return;
    await session.seek(nextTick);
    setTick(session.currentTick);
  }, []);

  const play = useCallback(async () => {
    const session = adapterRef.current.getPreviewSession();
    if (!session) return;
    setPlaying(true);
    await session.play();
    setPlaying(false);
  }, []);

  const pause = useCallback(async () => {
    const session = adapterRef.current.getPreviewSession();
    if (!session) return;
    await session.pause();
    setPlaying(false);
    setTick(session.currentTick);
  }, []);

  const exportCardTheme = resolveResultCardTheme({
    purchases,
    equippedTheme,
  });

  const exportReel = useCallback(async () => {
    const adapter = adapterRef.current;
    setPlaying(false);
    const result = await adapter.exportReel({
      sourcePack: pack,
      editGraph: graph,
      resolved,
      width: 360,
      height: 640,
      outputTickRate: pack.tickRate,
      resultCardScoreText: `Reach ${score.reach}`,
      resultCardTheme: exportCardTheme,
    });
    setTick(adapter.getPreviewSession()?.currentTick ?? 0);
    if (result.ok) {
      const blob =
        result.data instanceof Blob
          ? result.data
          : new Blob([new Uint8Array(result.data)], { type: result.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `desk-${packId}-result.png`;
      a.click();
      URL.revokeObjectURL(url);
      setExportNote(
        `Result card ${result.mimeType} (${Math.round(blob.size / 1024)} KiB). Watermarked PNG fallback, not deterministic video.`,
      );
    } else {
      setExportNote(`Export failed (${result.reason}). Fallback suggested.`);
    }
  }, [pack, packId, graph, resolved, score.reach, exportCardTheme]);

  return {
    packId,
    packTitle: entry.title,
    pack,
    graph,
    resolved,
    timeline,
    score,
    tick,
    playing,
    ready,
    error,
    exportNote,
    mountRef,
    adapter: adapterRef.current,
    unlocks,
    purchases,
    showReconstructionLabel,
    /** Expression sidegrades — overridden by EditorScreen from purchases/unlocks. */
    captionDeep: purchases.includes("caption-style-reveal"),
    soundDeep: unlocks.includes("soundDeep"),
    soundStingPack: purchases.includes("sound-sting-pack"),
    exportCardTheme,
    precisionTrim: purchases.includes("precision-trim"),
    loadPack,
    applyOp,
    applyCommand,
    seek,
    play,
    pause,
    exportReel,
    undo: () => applyCommand({ type: "undo" }),
    redo: () => applyCommand({ type: "redo" }),
    reset: () => applyCommand({ type: "reset", seed }),
    ops: activeOps(graph),
    canUndo: graph.cursor > 0,
    canRedo: graph.cursor < graph.ops.length,
  };
}

export type DeskSession = ReturnType<typeof useDeskSession>;
